/**
 * Welcome content types, language helpers and icon/style maps.
 *
 * The public landing content comes from portal-backend with separate
 * en/ar/he columns. Icons and colors are NOT stored in the backend — only
 * stable string keys are. These maps translate those keys into concrete
 * Lucide icons / Tailwind palettes. Keep the keys in sync with the backend
 * constants (courier-connect-app/landing-content/constants.ts).
 */

import {
  Bike,
  CheckCircle2,
  ClipboardList,
  FileCheck2,
  UserRound,
  Headphones,
  ScanLine,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';

// =============================================================================
// TYPES (match the portal-backend public landing-content response)
// =============================================================================

export interface WelcomeSettings {
  heroBadgeEn: string;
  heroBadgeAr: string;
  heroBadgeHe: string;
  heroHeadingPrefixEn: string;
  heroHeadingPrefixAr: string;
  heroHeadingPrefixHe: string;
  heroBrand: string;
  heroSubtitleEn: string;
  heroSubtitleAr: string;
  heroSubtitleHe: string;
  heroEstimateEn: string;
  heroEstimateAr: string;
  heroEstimateHe: string;
  heroCtaEn: string;
  heroCtaAr: string;
  heroCtaHe: string;
  timelineTitleEn: string;
  timelineTitleAr: string;
  timelineTitleHe: string;
  timelineSubtitleEn: string;
  timelineSubtitleAr: string;
  timelineSubtitleHe: string;
  didYouKnowTitleEn: string;
  didYouKnowTitleAr: string;
  didYouKnowTitleHe: string;
  didYouKnowSubtitleEn: string;
  didYouKnowSubtitleAr: string;
  didYouKnowSubtitleHe: string;
  couriersLabelEn: string;
  couriersLabelAr: string;
  couriersLabelHe: string;
  couriersValue: string;
  citiesLabelEn: string;
  citiesLabelAr: string;
  citiesLabelHe: string;
  citiesValue: string;
}

export interface WelcomeStep {
  id: number;
  titleEn: string;
  titleAr: string;
  titleHe: string;
  descriptionEn: string;
  descriptionAr: string;
  descriptionHe: string;
  iconKey: string;
  colorKey: string;
  sortOrder: number;
}

export interface WelcomeFeature {
  id: number;
  titleEn: string;
  titleAr: string;
  titleHe: string;
  descriptionEn: string;
  descriptionAr: string;
  descriptionHe: string;
  iconKey: string;
  sortOrder: number;
}

export interface WelcomeFact {
  id: number;
  textEn: string;
  textAr: string;
  textHe: string;
  sortOrder: number;
}

export interface WelcomeFaq {
  id: number;
  questionEn: string;
  questionAr: string;
  questionHe: string;
  answerEn: string;
  answerAr: string;
  answerHe: string;
  sortOrder: number;
}

export interface WelcomeContent {
  settings: WelcomeSettings;
  steps: WelcomeStep[];
  features: WelcomeFeature[];
  facts: WelcomeFact[];
  faq?: WelcomeFaq[];
  faqs?: WelcomeFaq[];
}

// =============================================================================
// LANGUAGE HELPER
// =============================================================================

export type WelcomeLang = 'en' | 'ar' | 'he';

/** Normalize an i18next language code to one of en/ar/he. */
export const normalizeLang = (lng: string | undefined): WelcomeLang => {
  const base = (lng || 'en').slice(0, 2).toLowerCase();
  if (base === 'ar') return 'ar';
  if (base === 'he') return 'he';
  return 'en';
};

/**
 * Pick the localized value from an object with En/Ar/He suffixed fields.
 * `base` is the field prefix, e.g. pick(step, 'title', 'he') -> step.titleHe.
 */
export const pick = <T extends object>(
  source: T,
  base: string,
  lang: WelcomeLang,
): string => {
  const suffix = lang === 'ar' ? 'Ar' : lang === 'he' ? 'He' : 'En';
  const value = (source as Record<string, unknown>)[`${base}${suffix}`];
  return typeof value === 'string' ? value : '';
};

// =============================================================================
// ICON / STYLE MAPS
// =============================================================================

export interface StepStyle {
  accent: string;
  bg: string;
  iconClass: string;
}

export const STEP_ICON_MAP: Record<string, LucideIcon> = {
  personal: UserRound,
  vehicle: Bike,
  experience: ClipboardList,
  documents: FileCheck2,
  review: CheckCircle2,
};

export const STEP_COLOR_MAP: Record<string, StepStyle> = {
  red: {
    accent: '#BF0035',
    bg: 'bg-haat-red-50',
    iconClass: 'text-haat-red',
  },
  orange: { accent: '#FF6B35', bg: 'bg-orange-50', iconClass: 'text-orange-500' },
  amber: { accent: '#F59E0B', bg: 'bg-amber-50', iconClass: 'text-amber-500' },
  emerald: {
    accent: '#10B981',
    bg: 'bg-emerald-50',
    iconClass: 'text-emerald-500',
  },
  violet: { accent: '#8B5CF6', bg: 'bg-violet-50', iconClass: 'text-violet-500' },
};

export const FEATURE_ICON_MAP: Record<string, LucideIcon> = {
  scan: ScanLine,
  guidance: Headphones,
  secure: ShieldCheck,
};

export const getStepIcon = (iconKey: string): LucideIcon =>
  STEP_ICON_MAP[iconKey] ?? UserRound;

export const getStepStyle = (colorKey: string): StepStyle =>
  STEP_COLOR_MAP[colorKey] ?? STEP_COLOR_MAP.red;

export const getFeatureIcon = (iconKey: string): LucideIcon =>
  FEATURE_ICON_MAP[iconKey] ?? ScanLine;
